import React, {
    useContext,
} from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';
import { analyticService } from 'global/services';

import { useProductImpressionObserver } from 'global/hooks/useProductImpressionObserver';
import { ConfigContext } from '../ConfigProvider';
import { PromoCheckoutPatchedDishesContext } from '../PromoCheckoutMenuDishesAdapter';

import DishCategory from './DishCategory';
import { DishCategorySecondary } from './DishCategorySecondary';
import { DishCategoryPrimary } from './DishCategoryPrimary';
import { MenuDishesContext } from '../PromoCheckoutMenuDishesProvider';


const firstCategoryNode = document.querySelector('[data-first-category-container]');
const restCategoriesNode = document.querySelector('[data-rest-categories-container]');

function DishCategories(props) {
    const {
        basketDishes,
        buttonsDisabled,
    } = props;

    const {
        pageId,
        dishCategories,
        canChangePortionsCount,
        areDetailsEnabled,
        smallCategoryTitlesInMobile,
        isNutritionEnabled,
    } = useContext(ConfigContext);

    const { dishesArray } = useContext(PromoCheckoutPatchedDishesContext);
    // Мержу два массива, т.к. не нашел, где затирается клиентское поле metaLabel
    const { dishes: dishesWithMetaLabel } = useContext(MenuDishesContext);

    const currentDishesArray = dishesArray.map((e) => {
        const target = dishesWithMetaLabel.find((elWithMeta) => e.id === elWithMeta.id);
        if (target && !e.metaLabel) {
            e.metaLabel = target.metaLabel;
        }
        return e;
    });

    const categoryIds = Object.keys(dishCategories);

    const sortedDishesByCategories = categoryIds.reduce(
        (acc, categoryId) => {
            const categoryDishes = currentDishesArray.filter((dish) => dish.category === categoryId);
            return [
                ...acc,
                ...categoryDishes,
            ];
        }, [],
    );

    const handleSendAnalytics = (intersectingDish) => {
        const intersectingDishIndex = sortedDishesByCategories
            ?.findIndex((e) => e.id === intersectingDish[0]?.id);
        analyticService.push({
            eventName: 'Product_Impression',
            dish: intersectingDish,
            dishIndex: intersectingDishIndex > -1
                ? intersectingDishIndex + 1
                : null,
            source_name: pageId,
        });
    };

    const [promoCheckoutDishObserver] = useProductImpressionObserver({
        dishesArray: currentDishesArray,
        analyticsHandler: handleSendAnalytics,
    });

    const isDishesDataEmpty = dishesArray.length === 0;
    if (isDishesDataEmpty) return null;

    return (
        <>
            {categoryIds.map((categoryId, index) => {
                const {
                    title,
                    titleEn,
                    description,
                    descriptionEn,
                    DescriptionComponent,
                    colors,
                    renderOnlyDishes,
                    layout,
                    portalSelector,
                    enabledDishesByBasket,
                } = dishCategories[categoryId];

                const categoryDishes = currentDishesArray.filter((dish) => dish.category === categoryId);
                const isFirstCategory = index === 0;

                if (categoryId === 'primary') {
                    return (
                        <DishCategoryPrimary
                            key={categoryId}
                            colors={colors}
                            dishes={categoryDishes}
                            basketDishes={basketDishes}
                            promoCheckoutDishObserver={promoCheckoutDishObserver}
                        />
                    );
                }

                const node = (() => {
                    if (portalSelector) return document.querySelector(portalSelector);
                    return isFirstCategory ? firstCategoryNode : restCategoriesNode;
                })();

                if (layout === 'secondary') {
                    return (
                        <Portal
                            node={node}
                            key={categoryId}
                        >
                            <DishCategorySecondary
                                colors={colors}
                                title={title}
                                titleEn={titleEn}
                                dishes={categoryDishes}
                                basketDishes={basketDishes}
                                buttonsDisabled={buttonsDisabled}
                                canChangePortionsCount={canChangePortionsCount}
                                promoCheckoutDishObserver={promoCheckoutDishObserver}
                            />
                        </Portal>
                    );
                }

                if (layout === 'newCards') {
                    return (
                        <Portal
                            node={isFirstCategory ? firstCategoryNode : restCategoriesNode}
                            key={categoryId}
                        >
                            <DishCategory
                                id={categoryId}
                                title={title}
                                titleEn={titleEn}
                                description={description}
                                descriptionEn={descriptionEn}
                                DescriptionComponent={DescriptionComponent}
                                dishes={categoryDishes}
                                isFirstCategory={isFirstCategory || renderOnlyDishes}
                                isLastCategory={!isFirstCategory && index === categoryIds.length - 1}
                                basketDishes={basketDishes}
                                buttonsDisabled={buttonsDisabled}
                                canChangePortionsCount={canChangePortionsCount}
                                areDetailsEnabled={areDetailsEnabled}
                                smallCategoryTitlesInMobile={smallCategoryTitlesInMobile}
                                colors={colors}
                                promoCheckoutDishObserver={promoCheckoutDishObserver}
                                isNutritionEnabled={isNutritionEnabled}
                                enabledDishesByBasket={enabledDishesByBasket}
                            />
                        </Portal>
                    );
                }

                return (
                    <Portal
                        node={isFirstCategory ? firstCategoryNode : restCategoriesNode}
                        key={categoryId}
                    >
                        <DishCategory
                            id={categoryId}
                            title={title}
                            titleEn={titleEn}
                            description={description}
                            descriptionEn={descriptionEn}
                            DescriptionComponent={DescriptionComponent}
                            dishes={categoryDishes}
                            isFirstCategory={isFirstCategory || renderOnlyDishes}
                            isLastCategory={!isFirstCategory && index === categoryIds.length - 1}
                            basketDishes={basketDishes}
                            buttonsDisabled={buttonsDisabled}
                            canChangePortionsCount={canChangePortionsCount}
                            areDetailsEnabled={areDetailsEnabled}
                            smallCategoryTitlesInMobile={smallCategoryTitlesInMobile}
                            colors={colors}
                            promoCheckoutDishObserver={promoCheckoutDishObserver}
                            isNutritionEnabled={isNutritionEnabled}
                            enabledDishesByBasket={enabledDishesByBasket}
                        />
                    </Portal>
                );
            })}
        </>
    );
}


DishCategories.propTypes = {
    basketDishes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    buttonsDisabled: PropTypes.bool.isRequired,
};

export default React.memo(DishCategories);
